import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Input } from '@shared/ui/Input';
import { Button } from '@shared/ui/Button';
import { SocialButton } from '@shared/ui/SocialButton';
import { useAuth } from '../model/useAuth';
import { useNavigate } from '@tanstack/react-router';

interface RegisterFormProps {
    onToggleMode?: () => void;
    showToggle?: boolean;
}

/**
 * Register Form Component - Fully Responsive
 * Handles: User registration with email/password, OAuth providers
 */
export function RegisterForm({ onToggleMode, showToggle = true }: RegisterFormProps) {
    const { t } = useTranslation();
    const { register, loginWithProvider } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<{
        fullName?: string;
        email?: string;
        password?: string;
        confirmPassword?: string;
    }>({});
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false,
        receiveUpdates: false,
    });

    const validateForm = () => {
        const newErrors: typeof errors = {};

        // Full Name validation
        if (!formData.fullName.trim()) {
            newErrors.fullName = t('auth.errors.nameRequired', 'Full name is required');
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email) {
            newErrors.email = t('auth.errors.emailRequired', 'Email is required');
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = t('auth.errors.emailInvalid', 'Invalid email format');
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = t('auth.errors.passwordRequired', 'Password is required');
        } else if (formData.password.length < 8) {
            newErrors.password = t('auth.errors.passwordMinLength', 'Password must be at least 8 characters');
        }

        // Confirm Password validation
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = t('auth.errors.passwordsDoNotMatch', 'Passwords do not match');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Validate terms acceptance
        if (!formData.acceptTerms) {
            // This shouldn't happen as button is disabled, but for safety
            return;
        }

        setLoading(true);

        try {
            await register(formData.fullName, formData.email, formData.password);
            navigate({ to: '/dashboard' });
        } catch (error: any) {
            console.error('Registration failed:', error);
            // Handle specific registration errors (e.g., email already exists)
            setErrors({
                email: error.message || t('auth.errors.registrationFailed', 'Registration failed. Please try again.'),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSocialAuth = async (provider: 'google' | 'github') => {
        try {
            await loginWithProvider(provider);
        } catch (error) {
            console.error(`${provider} login failed:`, error);
            // TODO: Show error message to user
        }
    };

    return (
        <div className="w-full space-y-6">
            {/* Title - Responsive Typography */}
            <div className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-bold text-text-primary font-mono tracking-tight">
                    {t('auth.createAccount')}
                </h2>
                <p className="text-sm sm:text-base text-text-secondary">
                    {t('auth.registerSubtitle')}
                </p>
            </div>

            {/* Social Login Buttons - Full Width */}
            <div className="w-full space-y-3">
                <SocialButton
                    provider="google"
                    onClick={() => handleSocialAuth('google')}
                    type="button"
                    className="w-full"
                >
                    {t('auth.googleLogin')}
                </SocialButton>
                <SocialButton
                    provider="github"
                    onClick={() => handleSocialAuth('github')}
                    type="button"
                    className="w-full"
                >
                    {t('auth.githubLogin')}
                </SocialButton>
            </div>

            {/* Divider */}
            <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full divider" />
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="bg-surface px-3 sm:px-4 text-text-muted font-mono">
                        {t('auth.orContinueWith')}
                    </span>
                </div>
            </div>

            {/* Form - Responsive Spacing */}
            <form onSubmit={handleSubmit} className="w-full space-y-4 sm:space-y-5">
                <Input
                    id="fullName"
                    type="text"
                    label={t('auth.fullName')}
                    placeholder={t('auth.namePlaceholder')}
                    value={formData.fullName}
                    onChange={(e) => {
                        setFormData({ ...formData, fullName: e.target.value });
                        if (errors.fullName) setErrors({ ...errors, fullName: undefined });
                    }}
                    icon={<User className="h-4 w-4" />}
                    error={errors.fullName}
                    corners="asymmetric"
                    className="w-full"
                />

                <Input
                    id="email"
                    type="email"
                    label={t('auth.email')}
                    placeholder={t('auth.emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (errors.email) setErrors({ ...errors, email: undefined });
                    }}
                    icon={<Mail className="h-4 w-4" />}
                    error={errors.email}
                    corners="asymmetric"
                    className="w-full"
                />

                <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label={t('auth.password')}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={formData.password}
                    onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value });
                        if (errors.password) setErrors({ ...errors, password: undefined });
                    }}
                    icon={<Lock className="h-4 w-4" />}
                    rightElement={
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="hover:text-primary transition-colors focus:outline-none"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    }
                    error={errors.password}
                    corners="asymmetric"
                    className="w-full"
                />

                <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    label={t('auth.confirmPassword')}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={formData.confirmPassword}
                    onChange={(e) => {
                        setFormData({ ...formData, confirmPassword: e.target.value });
                        if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                    }}
                    icon={<Lock className="h-4 w-4" />}
                    error={errors.confirmPassword}
                    corners="asymmetric"
                    className="w-full"
                />

                {/* Checkboxes - Touch-Friendly */}
                <div className="w-full space-y-3 sm:space-y-4">
                    {/* Terms Acceptance Checkbox - Required */}
                    <label className="flex items-start gap-3 text-text-secondary cursor-pointer min-h-[44px]">
                        <input
                            type="checkbox"
                            checked={formData.acceptTerms}
                            onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                            className="mt-0.5 rounded border-border bg-surface text-primary focus:ring-primary focus:ring-offset-background w-4 h-4 flex-shrink-0"
                            required
                        />
                        <span className="text-sm font-mono">
                            {t('auth.acceptTerms')}{' '}
                            <a href="#" className="text-primary hover:text-primary-hover transition-colors">
                                {t('auth.termsOfService')}
                            </a>{' '}
                            {t('auth.and')}{' '}
                            <a href="#" className="text-primary hover:text-primary-hover transition-colors">
                                {t('auth.privacyPolicy')}
                            </a>
                        </span>
                    </label>

                    {/* Notifications Checkbox - Optional */}
                    <label className="flex items-start gap-3 text-text-secondary cursor-pointer min-h-[44px]">
                        <input
                            type="checkbox"
                            checked={formData.receiveUpdates}
                            onChange={(e) => setFormData({ ...formData, receiveUpdates: e.target.checked })}
                            className="mt-0.5 rounded border-border bg-surface text-primary focus:ring-primary focus:ring-offset-background w-4 h-4 flex-shrink-0"
                        />
                        <span className="text-sm font-mono">
                            {t('auth.receiveUpdates')}
                        </span>
                    </label>
                </div>

                {/* Submit Button - Full Width, disabled until terms are accepted */}
                <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    corners="asymmetric"
                    loading={loading}
                    disabled={!formData.acceptTerms}
                    className="w-full mt-6"
                >
                    {t('auth.registerButton')}
                </Button>
            </form>

            {/* Toggle to Login - Responsive Text */}
            {showToggle && onToggleMode && (
                <div className="text-center text-sm">
                    <span className="text-text-secondary font-mono">
                        {t('auth.hasAccount')}{' '}
                    </span>
                    <button
                        onClick={onToggleMode}
                        type="button"
                        className="text-primary hover:text-primary-hover font-mono font-semibold transition-colors min-h-[44px] inline-flex items-center"
                    >
                        {t('auth.signInLink')}
                    </button>
                </div>
            )}
        </div>
    );
}
